import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {authenticateUser, isUserLoggedIn} from '@Libraries/user.library';
import {Button, Type} from '@Components/button';
import {openStockMediaModal} from '@Modals/stock-modal';
import type {ElementData} from '@Libraries/add-media-library';
import {UploadsMediaType, USER_UPLOADS_MEDIA_TABS} from '@Panels/user-media-panel/user-media-panel.types';
import {useSelectedTab} from '@Components/tabs';
import {getAllStockPanelTabs, getStockTabForUploadsTab} from '@Panels/user-media-panel/user-media.library';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {noop} from '@Utils/general.util';
import {linkMediaItems} from '@Libraries/user-media-library';
import {useAppSelector} from '@/hooks';

export interface NoMediaSectionProps {
  addInBulk: boolean;
  mediaTypesToShow: Array<UploadsMediaType>;
  maxSelectionNumber?: number;
  videoQualityFiltersDisabled?: boolean;
  shouldLoadDataUrlImage?: boolean;

  addSelectedItems(items?: Array<ElementData>): void;

  onClose(): void;
}

export function NoMediaSection({videoQualityFiltersDisabled = false, shouldLoadDataUrlImage = false, ...props}: NoMediaSectionProps): ReactElement | null {
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, UploadsMediaType.IMAGE) as UploadsMediaType;
  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const unlinkedItems = useAppSelector((state) => {
    return state.mediaUploads.unlinkedMedia;
  });
  const getStockTabsToShowForUploadTabs = (): Array<StockPanelTabs> => {
    const toShow: Array<StockPanelTabs> = [];

    if (props.mediaTypesToShow.length === 3) {
      return getAllStockPanelTabs();
    }

    props.mediaTypesToShow.forEach((tab): void => {
      toShow.push(getStockTabForUploadsTab(tab));
    });

    if (toShow.includes(StockPanelTabs.PHOTOS)) {
      toShow.push(StockPanelTabs.TEXT_TO_IMAGE);
    }

    return toShow;
  };

  return (
    <div className="flex-column-align-center flex-content-center _full-height _full-width">
      <Icon icon="icon-media" type={IconType.FLAT} className="content-body" size={IconSize.SIZE_ICON_32} shape={IconShape.CIRCLE} />
      <Text
        className="spacing-p-t-5 content-body"
        val={currentCollectionId ? window.i18next.t('pmwjs_empty_collection') : window.i18next.t('pmwjs_no_media_message')}
        size={TextSize.SMALL}
      />
      <div className="flexbox flex-items-center spacing-m-t-5">
        {!isUserLoggedIn() && !unlinkedItems.length ? (
          <Button
            text={window.i18next.t('pmwjs_user_login_action')}
            icon="icon-sign-in"
            customClasses="spacing-m-r-2"
            type={Type.SECONDARY}
            onClick={(): void => {
              void authenticateUser(linkMediaItems, noop, {
                fbPermissions: '',
                signupSource: 'default',
              });
            }}
          />
        ) : null}
        <Button
          text={window.i18next.t('pmwjs_add_stock_media')}
          type={Type.SECONDARY}
          icon="icon-medium"
          onClick={(): void => {
            props.onClose();
            return openStockMediaModal({
              onAddSelected: (items: Array<ElementData>): void => {
                props.addSelectedItems(items);
              },
              addInBulk: props.addInBulk,
              maxSelectionNumber: props.maxSelectionNumber,
              defaultSearchTab: getStockTabForUploadsTab(activeTab),
              tabsToShow: getStockTabsToShowForUploadTabs(),
              videoQualityFiltersDisabled,
              showTextToImageTool: true,
              hideOnClose: false,
              shouldLoadDataUrlImage,
              panelId: 'STOCK_PANEL_VIA_NO_MEDIA',
            });
          }}
        />
      </div>
    </div>
  );
}
